import React, { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';

import CropSquareIcon from '@mui/icons-material/CropSquare';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import PolylineIcon from '@mui/icons-material/Polyline';
import { Box, IconButton, Popover, Tooltip, Typography } from '@mui/material';

import {
	PrimaryButton,
	RED_COLOR_SX,
} from 'src/components/common/buttons/PrimaryButton';
import { ConfirmationModal } from 'src/components/common/modal/ConfirmationModal';
import { IAnnotationToolbarProps } from 'src/features/annotation/domain/types';
import {
	ANNOTATION_LOADING_KEY,
	updateAnnotation,
} from 'src/features/annotation/store/annotationThunks';
import { AppDispatch, RootState } from 'src/store';

export const AnnotationToolbar: React.FC<IAnnotationToolbarProps> = ({
	tool,
	setTool,
	currentAnnotation,
	onCurrentAnnotationCancel,
	disabled = false,
}) => {
	const { siteSlug } = useParams<{ siteSlug: string }>();
	const dispatch = useDispatch<AppDispatch>();
	const isLoading = useSelector(
		(state: RootState) =>
			state.loading.loadingStates[ANNOTATION_LOADING_KEY],
	);

	const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
	const [isModalOpen, setIsModalOpen] = useState(false);

	const handleUngradableClick = () => {
		if (!currentAnnotation || !siteSlug) return;

		dispatch(
			updateAnnotation({
				annotationId: currentAnnotation.id,
				request: { status: 'ungradable' },
				siteSlug: siteSlug,
				onSuccess: () => {
					onCurrentAnnotationCancel({
						...currentAnnotation,
						status: 'ungradable',
					});
					setIsModalOpen(false);
				},
			}),
		);
	};

	return (
		<>
			<Tooltip
				title={disabled ? 'Not allowed to annotate' : ''}
				placement='top-start'
			>
				<Box
					sx={{
						height: 60,
						display: 'flex',
						alignItems: 'center',
						gap: 1,
						backgroundColor: 'white',
						boxShadow: 1,
						borderRadius: 2,
						border: '1px solid',
						borderColor: 'grey.400',
						p: 1,
						mb: 2,
						cursor: disabled ? 'not-allowed' : 'default',
						position: 'relative',
					}}
				>
					{/* Select Tool */}
					<IconButton
						onClick={() => setTool('select')}
						disabled={disabled}
					>
						<img
							src='/cursor.svg'
							alt='Cursor'
							style={{
								width: 25,
								height: 25,
								opacity:
									tool === 'select' && !disabled ? 1 : 0.5,
								filter:
									tool === 'select' && !disabled
										? 'drop-shadow(0 0 4px #1976d2)'
										: 'none',
							}}
						/>
					</IconButton>

					{/* Rectangle Tool */}
					<IconButton
						color={tool === 'rectangle' ? 'primary' : 'default'}
						onClick={() => setTool('rectangle')}
						disabled={disabled}
					>
						<CropSquareIcon />
					</IconButton>

					{/* Polygon Tool */}
					<IconButton
						color={tool === 'polygon' ? 'primary' : 'default'}
						onClick={() => setTool('polygon')}
						disabled={disabled}
					>
						<PolylineIcon />
					</IconButton>

					{!disabled &&
						currentAnnotation?.status !== 'ungradable' && (
							<PrimaryButton
								size='medium'
								fullWidth={false}
								onClick={() => {
									setIsModalOpen(true);
								}}
								sx={{
									height: '40px',
									marginLeft: 'auto',
									marginRight: '40px',
									...RED_COLOR_SX,
								}}
								isLoading={isLoading}
							>
								MARK IMAGE AS UNGRADABLE
							</PrimaryButton>
						)}

					{currentAnnotation?.status === 'ungradable' && (
						<Typography
							variant='body2'
							color='error'
							sx={{
								marginLeft: 'auto',
								fontStyle: 'italic',
								backgroundColor: (theme) =>
									theme.palette.grey[100],
								padding: '4px 8px',
								borderRadius: '6px',
								marginRight: '40px',
							}}
						>
							This image was marked as ungradable
						</Typography>
					)}

					<IconButton
						onClick={(e) => setAnchorEl(e.currentTarget)}
						sx={{
							position: 'absolute',
							top: 15,
							right: 10,
							zIndex: 10,
						}}
						size='small'
					>
						<InfoOutlinedIcon fontSize='small' />
					</IconButton>

					<Popover
						open={Boolean(anchorEl)}
						anchorEl={anchorEl}
						onClose={() => setAnchorEl(null)}
						anchorOrigin={{
							vertical: 'bottom',
							horizontal: 'left',
						}}
						transformOrigin={{
							vertical: 'top',
							horizontal: 'right',
						}}
						translate='yes'
						sx={{ mt: 2 }}
					>
						<Box sx={{ p: 2, maxWidth: 400 }}>
							<Typography
								sx={{ fontSize: '16px', mb: 1 }}
								fontWeight={600}
							>
								Tools
							</Typography>
							<Typography variant='body2'>
								<strong>Zoom In/Out</strong> - Scroll Wheel
							</Typography>
							<Typography variant='body2'>
								<strong>Pan</strong> - SHIFT + Left Mouse
							</Typography>
						</Box>
					</Popover>
				</Box>
			</Tooltip>

			<ConfirmationModal
				title={<>Are you sure you want to mark image as ungradable?</>}
				modalContent={'This action cannot be undone.'}
				modalOpen={isModalOpen}
				handleYes={() => {
					handleUngradableClick();
				}}
				loadingKey={ANNOTATION_LOADING_KEY}
				handleNo={() => {
					setIsModalOpen(false);
				}}
				width='480px'
			/>
		</>
	);
};
